// Stick-to-bottom for a scroll container (chat transcripts). Follows growing
// content — new messages AND streaming tokens (which mutate the last message in
// place) — via a MutationObserver, but only while the user is already near the
// bottom. If they scroll up to read history we leave them there and expose
// `atBottom=false` so the surface can show a "jump to latest" button.
import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue';

export function useStickToBottom(container: Ref<HTMLElement | null>) {
  const atBottom = ref(true);
  // px of slack: treat "within 80px of the bottom" as at-bottom so a stray
  // pixel or the input's resize doesn't flip the state.
  const THRESHOLD = 80;

  function compute(): void {
    const el = container.value;
    if (el) atBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= THRESHOLD;
  }

  function scrollToBottom(smooth = true): void {
    const el = container.value;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    atBottom.value = true;
  }

  let mo: MutationObserver | null = null;
  const onScroll = (): void => compute();

  onMounted(() => {
    const el = container.value;
    if (!el) return;
    el.addEventListener('scroll', onScroll, { passive: true });
    // Any DOM change inside the transcript (appended message, streamed token,
    // an inline app rendering) → if the user was at the bottom, keep them there.
    mo = new MutationObserver(() => { if (atBottom.value) scrollToBottom(false); });
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    scrollToBottom(false);
  });

  onBeforeUnmount(() => {
    container.value?.removeEventListener('scroll', onScroll);
    mo?.disconnect();
  });

  return { atBottom, scrollToBottom };
}
